import React, { useState } from 'react';
import { Button, Card, CardBody, LoadingSpinner, MarkdownRenderer } from '../common';
import { PassageWithAttribution } from './PassageWithAttribution';
import { MVPVoteCard } from './MVPVoteCard';
import { VotingStatus } from './VotingStatus';
import { GMPickSelector } from '../completion/GMPickSelector';
import { usePassage } from '../../hooks/usePassages';
import {
  useMyVote,
  useCastVote,
  useVotingStatus,
  useVotableParticipants
} from '../../hooks/useVoting';
import { useCompleteRound, useAwardGMPick, useRoundResults } from '../../hooks/useRounds';

/**
 * VotingInterface - Unified voting phase view for both players and GM
 *
 * Player View:
 * - Cast Your MVP Vote at top
 * - GM Prompt
 * - Winning Detail (if any)
 * - Published Passage
 *
 * GM View:
 * - Complete Round / GM Pick selector at top
 * - GM Prompt
 * - Winning Detail (if any)
 * - Published Passage
 *
 * Note: VotingProgress is rendered separately as a full-width banner
 */
export const VotingInterface = ({ gameId, roundId, isGM, prompt, winningDetail }) => {
  const [completionStep, setCompletionStep] = useState('initial'); // 'initial', 'picking', 'complete'
  const [completionData, setCompletionData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch passage data
  const { data: passageData, isLoading: passageLoading } = usePassage(gameId, roundId);

  // Fetch voting data
  const { data: voteData, isLoading: voteLoading } = useMyVote(gameId, roundId);
  const { data: participantsData, isLoading: participantsLoading } = useVotableParticipants(gameId, roundId);
  const { data: votingStatus, isLoading: votingStatusLoading } = useVotingStatus(gameId, roundId);

  // GM completion hooks
  const { data: resultsData, isLoading: resultsLoading } = useRoundResults(gameId, roundId);
  const completeRound = useCompleteRound(gameId, roundId);
  const awardGMPick = useAwardGMPick(gameId, roundId);

  // Mutation for casting vote
  const castVote = useCastVote(gameId, roundId);

  const isLoading = passageLoading || voteLoading || participantsLoading || votingStatusLoading || (isGM && resultsLoading);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const passage = passageData?.passage;
  const myVote = voteData?.vote;
  const canVote = voteData?.canVote;
  const participants = participantsData?.participants || [];
  const allVotesIn = votingStatus?.allVotesIn;

  const handleVote = async (mvpUserId) => {
    try {
      await castVote.mutateAsync(mvpUserId);
    } catch (err) {
      console.error('Failed to cast vote:', err);
    }
  };

  const handleCompleteRound = async () => {
    setError(null);
    try {
      const result = await completeRound.mutateAsync();
      setCompletionData(result);
      setCompletionStep('picking');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete round');
    }
  };

  const handleGMPick = async (userId) => {
    setError(null);
    try {
      await awardGMPick.mutateAsync(userId);
      setCompletionStep('complete');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to award GM Pick');
    }
  };

  // If round is already complete, show results
  if (resultsData?.isComplete) {
    return (
      <div className="space-y-4">
        <RoundResults results={resultsData} />

        {/* GM Prompt */}
        {prompt && (
          <Card>
            <CardBody>
              <h3 className="font-semibold mb-2">
                {isGM ? 'Your Prompt' : 'GM Prompt'}
              </h3>
              <div className="prose prose-sm max-w-none">
                {prompt.mediaType === 'text' && (
                  <div className="text-gray-700">
                    <MarkdownRenderer content={prompt.content} />
                  </div>
                )}
                {prompt.mediaType === 'image' && prompt.mediaUrl && (
                  <img
                    src={prompt.mediaUrl}
                    alt="GM Prompt"
                    className="rounded-lg max-w-full h-auto"
                  />
                )}
                {prompt.mediaType === 'audio' && prompt.mediaUrl && (
                  <audio controls className="w-full">
                    <source src={prompt.mediaUrl} />
                  </audio>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Winning Detail */}
        {winningDetail && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">!</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 mb-1">Winning Detail</h3>
                <p className="text-amber-900 font-medium">"{winningDetail.content}"</p>
                <p className="text-sm text-amber-700 mt-1">
                  By {winningDetail.authorUserId?.displayName || 'Unknown'} · {winningDetail.totalBid || 1} bid{(winningDetail.totalBid || 1) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Published Passage */}
        <Card>
          <CardBody>
            <h3 className="font-semibold mb-4">Published Passage</h3>
            <div className="prose prose-sm max-w-none">
              <PassageWithAttribution
                passage={passage}
                participants={participants}
              />
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Player: Cast Your MVP Vote */}
      {!isGM && (
        <Card>
          <CardBody>
            <h3 className="font-semibold mb-3">Cast Your MVP Vote</h3>
            <p className="text-xs text-gray-500 mb-3">
              Vote for the player whose contribution you enjoyed the most.
              {myVote && ' You can change your vote until the round ends.'}
            </p>

            <div className="space-y-2">
              {participants.map((participant) => (
                <MVPVoteCard
                  key={participant.id}
                  participant={participant}
                  isSelected={myVote?.mvpUserId?.id === participant.id}
                  isSelf={participant.isSelf}
                  onVote={handleVote}
                  disabled={!canVote || castVote.isPending}
                  loading={castVote.isPending}
                />
              ))}
            </div>

            {myVote && (
              <div className="mt-3 p-2 bg-primary-50 rounded-lg">
                <p className="text-sm text-primary-700">
                  Your vote: <strong>{myVote.mvpUserId?.displayName}</strong>
                </p>
              </div>
            )}

            {/* Voting Progress */}
            <div className="mt-4 pt-4 border-t">
              <VotingStatus status={votingStatus} />
            </div>
          </CardBody>
        </Card>
      )}

      {/* GM: Complete Round / GM Pick */}
      {isGM && completionStep === 'initial' && (
        <Card>
          <CardBody>
            <h3 className="font-semibold mb-3">Complete This Round</h3>
            {!allVotesIn ? (
              <p className="text-gray-600 text-sm mb-4">
                Waiting for all votes to come in before the round can be completed.
              </p>
            ) : (
              <>
                <p className="text-gray-600 text-sm mb-4">
                  All votes are in! Calculate the MVP and select your GM Pick.
                </p>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                <Button
                  onClick={handleCompleteRound}
                  loading={completeRound.isPending}
                >
                  Calculate MVP & Select GM Pick
                </Button>
              </>
            )}

            {/* Voting Progress */}
            <div className="mt-4 pt-4 border-t">
              <VotingStatus status={votingStatus} />
            </div>
          </CardBody>
        </Card>
      )}

      {/* GM: Select GM Pick */}
      {isGM && completionStep === 'picking' && completionData && (
        <Card>
          <CardBody>
            <h3 className="font-semibold mb-3">Select GM Pick</h3>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                MVP will receive 1st place. Your GM Pick will receive 2nd place.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Total pot: {completionData.pot} Influence
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}

            <GMPickSelector
              candidates={completionData.gmPickCandidates}
              onSelect={handleGMPick}
              loading={awardGMPick.isPending}
              pot={completionData.pot}
            />
          </CardBody>
        </Card>
      )}

      {/* GM Prompt */}
      {prompt && (
        <Card>
          <CardBody>
            <h3 className="font-semibold mb-2">
              {isGM ? 'Your Prompt' : 'GM Prompt'}
            </h3>
            <div className="prose prose-sm max-w-none">
              {prompt.mediaType === 'text' && (
                <div className="text-gray-700">
                  <MarkdownRenderer content={prompt.content} />
                </div>
              )}
              {prompt.mediaType === 'image' && prompt.mediaUrl && (
                <img
                  src={prompt.mediaUrl}
                  alt="GM Prompt"
                  className="rounded-lg max-w-full h-auto"
                />
              )}
              {prompt.mediaType === 'audio' && prompt.mediaUrl && (
                <audio controls className="w-full">
                  <source src={prompt.mediaUrl} />
                </audio>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Winning Detail */}
      {winningDetail && (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">!</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800 mb-1">Winning Detail</h3>
              <p className="text-amber-900 font-medium">"{winningDetail.content}"</p>
              <p className="text-sm text-amber-700 mt-1">
                By {winningDetail.authorUserId?.displayName || 'Unknown'} · {winningDetail.totalBid || 1} bid{(winningDetail.totalBid || 1) !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Published Passage */}
      <Card>
        <CardBody>
          <h3 className="font-semibold mb-4">Published Passage</h3>
          <div className="prose prose-sm max-w-none">
            <PassageWithAttribution
              passage={passage}
              participants={participants}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

// Results display component
const RoundResults = ({ results }) => {
  return (
    <Card>
      <CardBody>
        <h3 className="font-semibold mb-4">Round Complete!</h3>

        <div className="space-y-3">
          {/* MVP Section */}
          {results.mvp && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-yellow-700 font-medium">MVP</p>
                  <p className="font-bold text-yellow-900">{results.mvp.displayName}</p>
                  <p className="text-xs text-yellow-600">{results.mvp.voteCount} vote{results.mvp.voteCount !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
          )}

          {/* GM Pick */}
          {results.gmPick && (
            <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-primary-700 font-medium">GM Pick</p>
                  <p className="font-bold text-primary-900">{results.gmPick.displayName}</p>
                  <p className="text-xs text-primary-600">+{results.gmPick.amount} influence</p>
                </div>
              </div>
            </div>
          )}

          {/* Vote Results */}
          {results.votes && results.votes.length > 0 && (
            <div className="pt-2">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Vote Results & Distribution</h4>
              <div className="space-y-1">
                {results.votes.map((voter, index) => (
                  <div
                    key={voter.userId}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-500 w-5">#{index + 1}</span>
                      <span>{voter.displayName}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">
                        {voter.voteCount} vote{voter.voteCount !== 1 ? 's' : ''}
                      </span>
                      {voter.distributionAmount > 0 && (
                        <span className="font-medium text-green-600">
                          +{voter.distributionAmount}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

/**
 * VotingProgressBanner - Full-width voting progress display
 * Shown above the main content during voting phase
 */
export const VotingProgressBanner = ({ gameId, roundId }) => {
  const { data: statusData, isLoading } = useVotingStatus(gameId, roundId);

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-2 bg-gray-200 rounded w-full"></div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <VotingStatus status={statusData} />
      </CardBody>
    </Card>
  );
};
